import React, { useEffect, useState } from "react";
import localStyles from "./index.module.css";
import commonStyles from '../../common/commonStyles/table.module.css';
import { Observer } from "../../dashboard";
import {Button, Form, Header, Icon, Loader, Menu, Message, Modal, Table} from "semantic-ui-react";
import agent, { PaginateOptions } from "../../../agent";
import { Link } from "react-router-dom";
import { Utils } from "../../../helpers/Utils";
import TablePage from "../../general/TablePage";
import { useUrlPagination } from "../../../hooks/useUrlPagination";
import ConfirmDeleteModal from "../../common/ConfirmDeleteModal";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    DEFAULT_SORT_COLUMN,
    handleSortPagination,
    ListState,
    ObjectReference,
} from "../../../helpers/TablePageUtils";
import { textLabels } from "../../../consts/textLabels";

interface ObserverPaginationResponse extends CommonListPagePaginationResponse<Observer> {}

const initialState: ListState<Observer> = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: "surname",
    searchValue: "",
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
};

const ObserversList: React.FC = () => {
    const currentPath = "/dashboard/observers";

    const { state, setState, updateUrl } = useUrlPagination<ListState<Observer>>(initialState, {
        defaultSortColumn: "surname",
    });

    const [searchInput, setSearchInput] = useState(state.searchValue);
    const [removeObject, setRemoveObject] = useState<ObjectReference | null>(null);

    const fetchList = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const paginateOptions: PaginateOptions = {
            page: state.page,
            limit: state.limit,
            sort: state.sortColumn,
            search: state.searchValue,
        };

        try {
            const response: ObserverPaginationResponse = await agent.ObserverProvider.getList(paginateOptions);

            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error("Ошибка получения:", error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: "Не удалось загрузить список наблюдателей. Пожалуйста, попробуйте позже.",
            }));
        }
    };

    useEffect(() => {
        fetchList();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    useEffect(() => {
        updateUrl();
    }, [state.page, state.limit, state.sortColumn, state.searchValue]);

    const handleSearch = () => {
        setState(prev => ({ ...prev, searchValue: searchInput, page: DEFAULT_PAGE }));
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setState(prev => ({ ...prev, searchValue: "", page: DEFAULT_PAGE }));
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const openRemoveModalHandler = (obj: ObjectReference) => {
        setRemoveObject(obj);
    };

    const closeRemoveModal = () => {
        setRemoveObject(null);
    };

    const removeObserver = async () => {
        if (!removeObject) return;

        setState(prev => ({ ...prev, loading: true }));

        try {
            await agent.ObserverProvider.remove(removeObject._id);
            fetchList();
        } catch (error) {
            console.error("Ошибка удаления:", error);
            setState(prev => ({
                ...prev,
                error: "Не удалось удалить наблюдателя. Пожалуйста, попробуйте позже.",
            }));
        } finally {
            closeRemoveModal();
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
    } = state;

    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = "Список наблюдателей";
    const confirmRemoveLabel = "Вы уверены, что хотите удалить наблюдателя";
    const removeLabel = textLabels.deleteButton.toUpperCase();
    const cancelLabel = textLabels.cancelButton;
    const createLabel = textLabels.createButton + " наблюдателя";
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            <ConfirmDeleteModal
                items={removeObject ? [removeObject] : []}
                confirmLabel={confirmRemoveLabel}
                onConfirm={removeObserver}
                onClose={closeRemoveModal}
            />

            <div className={styles.header}>
                <Header>{headerLabel}</Header>
                <Link to={`${currentPath}/create`}>
                    <Button
                        className={styles.createButton}
                        size="large"
                        color="green"
                        type="submit"
                    >
                        {createLabel}
                    </Button>
                </Link>
            </div>

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {loading ? (
                <Loader active>{listLoaderLabel}</Loader>
            ) : (
                <div className={styles.list}>
                    <Form>
                        <Form.Group>
                            <Form.Input
                                placeholder="ФИО"
                                iconPosition="left"
                                icon={<Icon name="search" />}
                                value={searchInput}
                                onChange={(e) => handleSearchInputChange(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                            <Form.Button primary content="Поиск" onClick={handleSearch} />
                            <Form.Button basic color="orange" content="Сбросить" onClick={handleClearSearch} />
                        </Form.Group>
                    </Form>

                    <div className={styles.container}>
                        <Table celled selectable striped verticalAlign="middle" color="teal">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell
                                        sorted={
                                            sortColumn === "surname" || sortColumn === "-surname"
                                                ? sortColumn.startsWith("-")
                                                    ? "descending"
                                                    : "ascending"
                                                : undefined
                                        }
                                        onClick={() => handleSort("surname")}
                                    >
                                        ФИО
                                    </Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {docs.map((el) => (
                                    <Table.Row style={{ cursor: "pointer" }} key={`observer-${el._id}`}>
                                        <Table.Cell floated="left">
                                            <Link
                                                className={styles.nameColumn}
                                                to={{
                                                    pathname: `${currentPath}/${el._id}/edit`,
                                                    search: `?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                                                }}
                                            >
                                                {`${el.surname} ${el.name} ${el.middlename}`}
                                            </Link>
                                        </Table.Cell>

                                        <Table.Cell floated="right" style={{ width: "5%" }}>
                                            <Button
                                                icon
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRemoveModalHandler({ _id: el._id, name: Utils.getFullName(el.surname, el.name, el.middlename) });
                                                }}
                                            >
                                                <Icon color="red" name="trash alternate outline" />
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>

                            <Table.Footer>
                                <Table.Row>
                                    <Table.HeaderCell colSpan="2">
                                        <Menu floated="right" pagination>
                                            <TablePage
                                                currentPage={page}
                                                totalPages={totalPages}
                                                totalDocs={totalDocs}
                                                limit={limit}
                                                onPageChange={handlePageChange}
                                                onLimitChange={handleLimitChange}
                                            />
                                        </Menu>
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Footer>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ObserversList;